import { Alert, Box, Modal, Text, Button } from '@nimbus-ds/components';
import { generatePath } from 'react-router';
import { useNavegate } from 'App/hooks';
import { CancelAndConfirmButtons } from 'commons/components';
import { useTranslationMarketing } from 'domains/Marketing/hook';
import { promotionHasLegacyDiscountConflict } from '../../components/PaymentOptions/utils';
import { useGetPaymentOptionsForDiscountConfiguration } from '../hooks';
import { useGetPromotionQuery } from '../promotionsApi/promotionsApi';
import { PROMOTIONS_ROUTES } from '../promotionsRoutes';

const { Header, Body, Footer } = Modal;

interface StatusPromotionModalProps {
  show: boolean;
  isActive: boolean;
  promotionId: string;
  onClose: () => void;
  onChangeStatus: () => void;
}

export function StatusPromotionModal({
  show,
  isActive,
  promotionId,
  onClose,
  onChangeStatus,
}: Readonly<StatusPromotionModalProps>) {
  const prefix = !isActive ? 'enable' : 'disable';
  const t = useTranslationMarketing(`promotions.statusModal.${prefix}`);
  const { goTo } = useNavegate();

  const { data: promotion, isLoading: isPromotionLoading } =
    useGetPromotionQuery(promotionId);
  const {
    paymentOptions: activePaymentOptions,
    isLoading: isPaymentOptionsLoading,
  } = useGetPaymentOptionsForDiscountConfiguration();

  const isLoadingConflictData =
    !isActive && (isPromotionLoading || isPaymentOptionsLoading);

  const hasConflict =
    !isActive &&
    promotion &&
    promotionHasLegacyDiscountConflict(promotion, activePaymentOptions);

  const handleChangeStatus = () => {
    onChangeStatus();
    onClose();
  };

  const handleGoToEdit = () => {
    onClose();
    goTo(generatePath(PROMOTIONS_ROUTES.editPromotions, { id: promotionId }));
  };

  return (
    <Modal open={show} onDismiss={onClose}>
      {hasConflict ? (
        <Header title={t('legacyDiscountConflict.title')} />
      ) : (
        <Header title={t('title')} />
      )}
      <Body padding="none">
        <Text>{t('body')}</Text>

        {hasConflict && (
          <Box marginTop="4">
            <Alert appearance="warning">
              <Text>{t('legacyDiscountConflict.text')}</Text>
            </Alert>
          </Box>
        )}
      </Body>
      <Footer>
        {hasConflict ? (
          <Box display="flex" gap="3" width="100%" justifyContent="flex-end">
            <Button appearance="neutral" onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button appearance="primary" onClick={handleGoToEdit}>
              {t('legacyDiscountConflict.edit')}
            </Button>
          </Box>
        ) : (
          <CancelAndConfirmButtons
            onCancel={onClose}
            onConfirm={handleChangeStatus}
            cancelText={t('cancel')}
            confirmText={t('confirm')}
            isConfirmDisabled={isLoadingConflictData}
            isLoading={isLoadingConflictData}
          />
        )}
      </Footer>
    </Modal>
  );
}
